import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const socialLinks = [
    {
      name: 'LinkedIn',
      href: '#',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
    {
      name: 'Facebook',
      href: '#',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    {
      name: 'Instagram',
      href: '#',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781c-.315 0-.612-.123-.833-.344-.221-.221-.344-.518-.344-.833 0-.315.123-.612.344-.833.221-.221.518-.344.833-.344.315 0 .612.123.833.344.221.221.344.518.344.833 0 .315-.123.612-.344.833-.221.221-.518.344-.833.344z"/>
        </svg>
      )
    }
  ];

  const productLinks = [
    { name: 'Vanille Bourbon Grade A', href: '#products' },
    { name: 'Vanille Bourbon Grade B', href: '#products' },
    { name: 'Café Robusta Grade 1', href: '#products' },
    { name: 'Épices Premium', href: '#products' }
  ];

  const companyLinks = [
    { name: t('navigation.story'), href: '#story' },
    { name: t('navigation.process'), href: '#process' },
    { name: 'Certifications', href: '#' },
    { name: 'Partenaires', href: '#' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-coffee-200 to-coffee-100 text-cream-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-40 h-40 bg-cream-50 rounded-full"></div>
        <div className="absolute bottom-32 right-32 w-32 h-32 bg-forest-200 rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-cream-50 rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="flex items-center space-x-4 mb-8">
              <img 
                src="/assets/Logo_EAM.png" 
                alt="EUDORA AROMAS MADAGASCAR" 
                className="h-16 w-auto filter drop-shadow-md"
                onError={(e) => {
                  // Fallback si le logo n'est pas encore ajouté
                  e.currentTarget.style.display = 'none';
                  if (e.currentTarget.nextElementSibling) {
                    (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                  }
                }}
              />
              <div className="w-16 h-16 bg-gradient-to-br from-forest-200 to-coffee-100 rounded-full flex items-center justify-center shadow-md" style={{display: 'none'}}>
                <span className="text-white font-display font-bold text-2xl">E</span>
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold gradient-text">EUDORA AROMAS</h3>
                <p className="text-cream-100 text-lg font-medium">MADAGASCAR</p>
                <p className="text-cream-100/80 text-sm">{t('footer.company.tagline')}</p>
              </div>
            </div>
            
            <p className="text-cream-100 leading-relaxed mb-8 max-w-md text-lg">
              {t('footer.company.description')}
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-12 h-12 bg-cream-50/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-cream-50/20 transition-all duration-300 border border-cream-50/20"
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Products Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="font-display text-xl font-semibold mb-6 text-cream-50">
              {t('footer.products')}
            </h4>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => scrollToSection(link.href)}
                    className="text-cream-100 hover:text-cream-50 transition-colors duration-300 text-left hover:translate-x-1 transform transition-transform"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="font-display text-xl font-semibold mb-6 text-cream-50">
              Entreprise
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => scrollToSection(link.href)}
                    className="text-cream-100 hover:text-cream-50 transition-colors duration-300 text-left hover:translate-x-1 transform transition-transform"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>

            {/* Certifications */}
            <div className="mt-8">
              <h5 className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-cream-100/70">Certifications</h5>
              <div className="grid grid-cols-3 gap-2" aria-label="Certifications Eudora Aromas">
                <div className="flex h-20 items-center justify-center rounded-xl border border-cream-50/15 bg-cream-50/10 p-2">
                  <img src="/assets/badge_100_organic.png" alt="100% Organic" className="max-h-14 w-full object-contain" />
                </div>
                <div className="flex h-20 items-center justify-center rounded-xl border border-cream-50/15 bg-cream-50/10 p-2">
                  <img src="/assets/badge_100_natural.png" alt="100% Natural" className="max-h-14 w-full object-contain" />
                </div>
                <div className="flex h-20 items-center justify-center rounded-xl border border-cream-50/15 bg-cream-50/10 p-3">
                  <img src="/assets/logo_fairtrade.png" alt="Fairtrade" className="h-full w-full object-contain" />
                </div>
                <div className="flex h-20 items-center justify-center rounded-xl border border-cream-50/15 bg-cream-50/10 p-3">
                  <img src="/assets/no_child_abuse.png" alt="Sans travail des enfants" className="h-full w-full object-contain" />
                </div>
                <div className="flex h-20 items-center justify-center rounded-xl border border-cream-50/15 bg-cream-50/10 p-2">
                  <img src="/assets/recycle.png" alt="Recyclable" className="max-h-14 w-full object-contain" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="border-t border-cream-50/20 mt-16 pt-8"
        >
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <p className="text-cream-100 text-sm text-center lg:text-left">
              © 2026 EUDORA AROMAS MADAGASCAR. {t('footer.rights')}.
            </p>
            
            <div className="flex flex-wrap justify-center lg:justify-end gap-6 text-sm">
              <button className="text-cream-100 hover:text-cream-50 transition-colors duration-300">
                {t('footer.legal')}
              </button>
              <button className="text-cream-100 hover:text-cream-50 transition-colors duration-300">
                {t('footer.privacy')}
              </button>
              <button className="text-cream-100 hover:text-cream-50 transition-colors duration-300">
                {t('footer.terms')}
              </button>
            </div>
          </div>

          {/* Made with love */}
          <div className="text-center mt-6 pt-6 border-t border-cream-50/10">
            <p className="text-cream-100/60 text-xs flex items-center justify-center">
              Made with 
              <Heart className="mx-1 h-4 w-4 text-red-400" fill="currentColor" aria-hidden="true" />
              By&nbsp;<a href='https://nomad-krea.space/'> Nomad Kréa</a>
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;